// client/src/components/event/AMAConfig.jsx
/**
 * =================================================================
 * AMA (Ask Me Anything) Configuration Component
 * =================================================================
 * Follows ChallengeConfig pattern for consistency.
 * FEATURES:
 * 1. Set host name, bio, platform
 * 2. Schedule date/time and duration
 * 3. Display shareable hyperlink
 * 4. View registrations and submitted questions
 * 5. Answer questions from admin panel
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import DateTimeSelect from "../DateTimeSelect";
import "./AuctionConfig.css";

const AMAConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "AMA",
    description: "",
    host_name: "",
    host_bio: "",
    platform: "",
    scheduled_at: "",
    duration_minutes: 120,
    status: "NEW",
  });

  const [registrations, setRegistrations] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [registrationCount, setRegistrationCount] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [configExists, setConfigExists] = useState(false);

  // Answer panel state
  const [answeringId, setAnsweringId] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [answerSaving, setAnswerSaving] = useState(false);
  const [questionFilter, setQuestionFilter] = useState("all"); // all | unanswered | answered

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = configExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=ama`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.ama) {
        const c = response.data.ama;
        setConfigExists(true);
        setConfig({
          name: c.name || itemName || "AMA",
          description: c.description || "",
          host_name: c.host_name || "",
          host_bio: c.host_bio || "",
          platform: c.platform || "",
          scheduled_at: formatDateForInput(c.scheduled_at),
          duration_minutes: c.duration_minutes || 120,
          status: c.status || "NEW",
        });
        loadRegistrationCount();
        loadQuestionCount();
      } else {
        setConfigExists(false);
        setMessage({
          text: "No AMA configured yet. Fill out the form below to create one.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading AMA config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "No AMA configured yet. Fill out the form below to create one.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load AMA configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrationCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/registrations`
      );
      if (response.data.success) {
        setRegistrationCount(response.data.count || response.data.registrations?.length || 0);
      }
    } catch { /* Non-critical */ }
  };

  const loadQuestionCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/questions`
      );
      if (response.data.success) {
        setQuestionCount(response.data.count || response.data.questions?.length || 0);
      }
    } catch { /* Non-critical */ }
  };

  const loadRegistrations = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/registrations`
      );
      if (response.data.success) {
        setRegistrations(response.data.registrations);
        setRegistrationCount(response.data.count || response.data.registrations.length);
        setShowRegistrations(true);
      }
    } catch (err) {
      console.error("Error loading registrations:", err);
      setMessage({ text: "Failed to load registrations", type: "error" });
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/questions`
      );
      if (response.data.success) {
        setQuestions(response.data.questions);
        setQuestionCount(response.data.count || response.data.questions.length);
        setShowQuestions(true);
      }
    } catch (err) {
      console.error("Error loading questions:", err);
      setMessage({ text: "Failed to load questions", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("AMA name is required");
    }
    if (config.duration_minutes < 1) {
      errors.push("Duration must be at least 1 minute");
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        description: config.description || null,
        host_name: config.host_name || null,
        host_bio: config.host_bio || null,
        platform: config.platform || null,
        scheduled_at: formatDateTimeForBackend(config.scheduled_at),
        duration_minutes: Number(config.duration_minutes),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setConfigExists(true);
        setMessage({ text: "AMA configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving AMA config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save AMA configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Cancel this AMA? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/config`,
        { ...config, status: "CANCELLED" }
      );
      if (response.data.success) {
        setMessage({ text: "AMA cancelled successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error cancelling AMA:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to cancel AMA",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAnswerSubmit = async (questionId) => {
    if (!answerText.trim()) return;

    setAnswerSaving(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/question/${questionId}/answer`,
        { answer: answerText.trim() }
      );

      if (response.data.success) {
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId
              ? { ...q, answer: answerText.trim(), answered: 1, answered_at: new Date().toISOString() }
              : q
          )
        );
        setAnsweringId(null);
        setAnswerText("");
      }
    } catch (err) {
      console.error("Error saving answer:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save answer",
        type: "error",
      });
    } finally {
      setAnswerSaving(false);
    }
  };

  const startAnswering = (q) => {
    setAnsweringId(q.id);
    setAnswerText(q.answer || "");
  };

  const filteredQuestions = questions.filter((q) => {
    if (questionFilter === "unanswered") return !q.answered;
    if (questionFilter === "answered") return q.answered;
    return true;
  });

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading AMA configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE" || config.status === "CANCELLED";

  return (
    <div className="auction-config-container">
      {/* Header */}
      <div className="auction-config-header">
        <h2>AMA Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Share Link */}
      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link for participants to register and submit questions:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {(config.name || itemName || "").replace(/\s+/g, "_")}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      {configExists && (
        <div className="auction-stats-section">
          <h3>AMA Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Registrations</div>
              <div className="stat-value">{registrationCount}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Questions Submitted</div>
              <div className="stat-value">{questionCount}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadRegistrations}>
              View Registrations
            </button>
            <button type="button" className="view-winners-button" onClick={loadQuestions}>
              View Questions
            </button>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">
            AMA Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter AMA name"
            disabled={isComplete}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe what this AMA is about"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="host_name">Host Name</label>
            <input
              type="text"
              id="host_name"
              value={config.host_name}
              onChange={(e) => handleInputChange("host_name", e.target.value)}
              placeholder="Name of the AMA host"
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="platform">Platform</label>
            <input
              type="text"
              id="platform"
              value={config.platform}
              onChange={(e) => handleInputChange("platform", e.target.value)}
              placeholder="e.g. Reddit, Instagram, Zoom"
              disabled={isComplete}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="host_bio">Host Bio</label>
          <textarea
            id="host_bio"
            value={config.host_bio}
            onChange={(e) => handleInputChange("host_bio", e.target.value)}
            placeholder="Short bio of the host"
            disabled={isComplete}
            rows={2}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="scheduled_at">Scheduled Date & Time</label>
            <DateTimeSelect
              id="scheduled_at"
              value={config.scheduled_at}
              onChange={(v) => handleInputChange("scheduled_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="duration_minutes">
              Duration (minutes) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="duration_minutes"
              min="1"
              max="1440"
              value={config.duration_minutes}
              onChange={(e) => handleInputChange("duration_minutes", parseInt(e.target.value) || 120)}
              disabled={isComplete}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              This AMA is {config.status === "CANCELLED" ? "cancelled" : "complete"} - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {configExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Cancel AMA
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {/* Registrations Modal */}
      {showRegistrations && (
        <div className="modal-overlay" onClick={() => setShowRegistrations(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Registrations ({registrations.length})</h3>
              <button className="close-button" onClick={() => setShowRegistrations(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, index) => (
                    <tr key={reg.id}>
                      <td>{index + 1}</td>
                      <td>{reg.name}</td>
                      <td>{reg.email}</td>
                      <td>{new Date(reg.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Questions & Answer Panel Modal */}
      {showQuestions && (
        <div className="modal-overlay" onClick={() => { setShowQuestions(false); setAnsweringId(null); setAnswerText(""); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "700px" }}>
            <div className="modal-header">
              <h3>Questions ({questions.length})</h3>
              <button className="close-button" onClick={() => { setShowQuestions(false); setAnsweringId(null); setAnswerText(""); }}>
                &times;
              </button>
            </div>

            {/* Filter controls */}
            <div style={{ display: "flex", gap: "8px", padding: "0 20px 12px", flexWrap: "wrap" }}>
              {["all", "unanswered", "answered"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setQuestionFilter(f)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "16px",
                    border: "1px solid " + (questionFilter === f ? "#4a90d9" : "#555"),
                    background: questionFilter === f ? "#4a90d9" : "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "13px",
                    textTransform: "capitalize",
                  }}
                >
                  {f} {f === "unanswered" ? `(${questions.filter((q) => !q.answered).length})` : f === "answered" ? `(${questions.filter((q) => q.answered).length})` : ""}
                </button>
              ))}
            </div>

            <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {filteredQuestions.length === 0 ? (
                <p style={{ textAlign: "center", color: "#999", padding: "20px" }}>
                  No {questionFilter !== "all" ? questionFilter + " " : ""}questions yet
                </p>
              ) : (
                filteredQuestions.map((q) => (
                  <div
                    key={q.id}
                    style={{
                      border: "1px solid #444",
                      borderRadius: "8px",
                      padding: "14px",
                      marginBottom: "12px",
                      background: q.answered ? "#2a3a2a" : "#333",
                    }}
                  >
                    {/* Question header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                      <div>
                        <strong style={{ color: "#ccc", fontSize: "13px" }}>{q.name}</strong>
                        <span style={{ color: "#888", fontSize: "12px", marginLeft: "8px" }}>
                          {new Date(q.created_on).toLocaleString()}
                        </span>
                      </div>
                      <span style={{ color: "#aaa", fontSize: "12px", whiteSpace: "nowrap" }}>
                        {q.upvotes || 0} upvote{q.upvotes !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Question text */}
                    <p style={{ color: "#fff", margin: "0 0 10px", lineHeight: "1.5" }}>{q.question}</p>

                    {/* Existing answer display */}
                    {q.answered && answeringId !== q.id ? (
                      <div style={{ background: "#1a2a1a", borderRadius: "6px", padding: "10px 12px", marginBottom: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                          <span style={{ color: "#4caf50", fontSize: "14px" }}>&#10003;</span>
                          <strong style={{ color: "#4caf50", fontSize: "13px" }}>Answered</strong>
                          {q.answered_at && (
                            <span style={{ color: "#888", fontSize: "11px", marginLeft: "4px" }}>
                              {new Date(q.answered_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p style={{ color: "#ddd", margin: 0, lineHeight: "1.5", fontSize: "14px" }}>{q.answer}</p>
                      </div>
                    ) : null}

                    {/* Answer input area */}
                    {answeringId === q.id ? (
                      <div style={{ marginTop: "8px" }}>
                        <textarea
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder="Type your answer..."
                          rows={3}
                          style={{
                            width: "100%",
                            padding: "10px",
                            borderRadius: "6px",
                            border: "1px solid #555",
                            background: "#2a2a2a",
                            color: "#fff",
                            fontSize: "14px",
                            resize: "vertical",
                            boxSizing: "border-box",
                          }}
                        />
                        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                          <button
                            type="button"
                            onClick={() => handleAnswerSubmit(q.id)}
                            disabled={answerSaving || !answerText.trim()}
                            style={{
                              padding: "8px 18px",
                              borderRadius: "6px",
                              border: "none",
                              background: answerSaving || !answerText.trim() ? "#555" : "#4a90d9",
                              color: "#fff",
                              cursor: answerSaving || !answerText.trim() ? "not-allowed" : "pointer",
                              fontSize: "13px",
                            }}
                          >
                            {answerSaving ? "Publishing..." : "Publish Answer"}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setAnsweringId(null); setAnswerText(""); }}
                            style={{
                              padding: "8px 18px",
                              borderRadius: "6px",
                              border: "1px solid #555",
                              background: "transparent",
                              color: "#ccc",
                              cursor: "pointer",
                              fontSize: "13px",
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startAnswering(q)}
                        style={{
                          padding: "6px 14px",
                          borderRadius: "6px",
                          border: "1px solid #555",
                          background: "transparent",
                          color: "#4a90d9",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        {q.answered ? "Edit Answer" : "Answer"}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AMAConfig;
